import { BigNumber } from '@ethersproject/bignumber';
import { DecoratedSilo, SiloAsset } from 'queries/decoratedSilo';
import { useMemo } from 'react';
import { useThisDecoratedSilo } from '../../silo';
import { MaxAmount } from '../../../../hooks/useMaxTokenAmount';
import { BigNumberWithDecimals, ProtocolInstance } from '@silo/types';

interface ValidateFormParams {
  decoratedSilo: DecoratedSilo;
  normalizedTokenAmount: BigNumber | null;
  amountInUSD: BigNumberWithDecimals | null;
  maxAmount: MaxAmount;
  selectedAsset: SiloAsset;
  isFullAmount: boolean;
  protocolInstance: ProtocolInstance;
  priceInUSD: BigNumberWithDecimals | null;
}

type ValidationResult = { message: string; submitMessage?: string } | null;

export type ValidateForm = (params: ValidateFormParams) => ValidationResult;

interface UseValidationProps extends ValidateFormParams {
  validateForm?: ValidateForm;
}

interface UseValidation {
  isInvalid: boolean;
  errorMessage?: string;
  submitButtonErrorMessage?: string;
}

export const useValidation = ({
  protocolInstance,
  validateForm,
  normalizedTokenAmount,
  maxAmount,
  selectedAsset,
  amountInUSD,
  isFullAmount,
  priceInUSD,
}: UseValidationProps): UseValidation => {
  const decoratedSilo = useThisDecoratedSilo();

  return useMemo(() => {
    if (!validateForm) return { isInvalid: false };

    const result = validateForm({
      normalizedTokenAmount,
      amountInUSD,
      maxAmount,
      selectedAsset,
      decoratedSilo,
      isFullAmount,
      protocolInstance,
      priceInUSD,
    });

    if (!result) return { isInvalid: false };

    return {
      isInvalid: true,
      errorMessage: result.message,
      submitButtonErrorMessage: result.submitMessage,
    };
  }, [
    validateForm,
    normalizedTokenAmount,
    selectedAsset,
    decoratedSilo,
    maxAmount,
    amountInUSD,
    isFullAmount,
    protocolInstance,
    priceInUSD,
  ]);
};
